import React, { useState, useRef, useEffect } from "react";
import PropTypes from "prop-types";
import { observer } from "mobx-react";
import classnames from "classnames/bind";

import Link from "@components/link";
import Icon from "@components/icon";

import MessageBody from "../MessageBody";
import ScenarioActions from "../ScenarioActions";

import interpolate from "../../../../locales/interpolate";

import ScenarioApiConstructor from "../../../../api/ScenarioApi";

import { notificationTypes } from "./consts";

import { useStore } from "../../../../connect";

import strings from "../../../../locales/strings";

import styles from "./Notification.scss";

const cx = classnames.bind(styles);

const ScenarioApi = new ScenarioApiConstructor("/webapi/scen/");

const formatDate = (date) => {
  const dateObject = new Date(date);
  const now = new Date();

  const year = dateObject.getFullYear();
  const month = dateObject.getMonth();
  const day = dateObject.getDate();
  const hours = dateObject.getHours();
  const minutes = dateObject.getMinutes();

  const thisYear = year === now.getFullYear();
  const thisMonth = month === now.getMonth();
  const thisDay = day === now.getDate();

  const dd = day < 10 ? `0${day}` : day;
  const hh = hours < 10 ? `0${hours}` : hours;
  const mm = minutes < 10 ? `0${minutes}` : minutes;
  const extendedDate = !thisDay || !thisMonth || !thisYear;

  const dateArr = [
    extendedDate && `${dd}\xa0${strings.shortMonths[month]}`,
    !thisYear && `\xa0${year}`,
    thisYear && thisMonth && thisDay && `${hh}:${mm}`,
  ];

  return dateArr.filter(Boolean).join("");
};

const Notification = ({ notification, reference }) => {
  const { baseUrl, notifications } = useStore();
  const { PartnerId, PartnerName, CanUnsubscribe } =
    notification.Advertisement || {};

  const settingsUrl = `https://${baseUrl}/notification-settings?utm_source=header&utm_campaign=new_header`;

  const [scenarios, setScenarios] = useState({
    scenario: null,
    error: false,
    success: false,
  });
  const [scenarioLoading, setScenarioLoading] = useState({});

  const mounted = useRef(true);

  const openNotificationLink = (event) => {
    event.preventDefault();
    event.stopPropagation();

    window.open(notification.DefaultActionUrl, "_blank");
  };

  const removeNotification = () => {
    if (notifications.onRemoveNotification) {
      notifications.onRemoveNotification(notification.ID);
    }
  };

  const handleNotificationClick = (event) => {
    event.stopPropagation();

    openNotificationLink(event);

    if (notification.IsUnread) {
      removeNotification();
    }
  };

  const provideRef = (ref) => mounted.current && reference && reference(ref);

  const handleScenarioAction = (scenario) => {
    const { ActionsParams, ID, IsUnread } = notification;

    if (!Object.keys(scenarioLoading).length) {
      const scenarioLoadingModiffied = { ...scenarioLoading };
      scenarioLoadingModiffied[scenario] = true;

      setScenarioLoading(scenarioLoadingModiffied);
      setScenarios({
        scenario,
        success: false,
        error: false,
      });

      ScenarioApi.scenarioAction(scenario, ActionsParams)
        .then(() => {
          if (mounted.current) {
            setScenarioLoading({});
            setScenarios({
              scenario,
              success: true,
              error: false,
            });

            if (notifications.readNotificationsDebounce && IsUnread) {
              notifications.readNotificationsDebounce(ID);
            }
          }
        })
        .catch(() => {
          if (mounted.current) {
            setScenarioLoading({});
            setScenarios({
              scenario,
              success: false,
              error: true,
            });
          }
        });
    }
  };

  const handleUnsubscribe = () => {
    notifications.unsubscribe(notification.ID, PartnerId, PartnerName);
  };

  const handleScenarioBack = () =>
    setTimeout(() => {
      setScenarios({
        scenario: scenarios.scenario,
        success: false,
        error: false,
      });
    }, 0);

  useEffect(() => {
    const { EridToken, PartnerInn, PartnerLegalName } =
      notification.Advertisement || {};

    if (PartnerLegalName && EridToken && PartnerInn) {
      window.setMark({
        badgeId: `ntf-mark-badge-${notification.ID}`,
        infoId: `ntf-mark-info-${notification.ID}`,
        name: PartnerLegalName,
        erid: EridToken,
        inn: PartnerInn,
        badge: "dark",
      });
    } else {
      console.warn(
        "Not enough data to mark notification: check fields with legal name, erid-token and inn"
      );
    }
  }, []);

  return (
    <>
      <div id={`ntf-mark-info-${notification.ID}`}>
        <div
          className={cx("message", {
            "message-red": notification.Priority === "High",
            unread: notification.IsUnread,
          })}
          role="presentation"
          ref={provideRef}
          data-name="notification-message"
        >
          <div className={cx("message-title")}>
            <Icon
              className={cx("notifications-icon")}
              name={
                notificationTypes[notification.Type]
                  ? notificationTypes[notification.Type].icon
                  : notificationTypes.System.icon
              }
              size="m"
              color={
                notificationTypes[notification.Type]
                  ? notificationTypes[notification.Type].color
                  : notificationTypes.System.color
              }
            />

            <div className={cx("message-content")} data-name="message-content">
              {/* <p className={cx("message-title-name")}>
        {this.props.notification.Subject}</p> */}
              <MessageBody
                message={notification.Message}
                partnerId={PartnerId}
                partnerName={PartnerName}
              />

              <div className={cx("message-footer")} data-name="message-footer">
                {notification.Actions && notification.Actions.length ? (
                  <ScenarioActions
                    notification={notification}
                    scenarios={scenarios}
                    scenarioLoading={scenarioLoading}
                    handleScenarioAction={handleScenarioAction}
                    handleScenarioBack={handleScenarioBack}
                  />
                ) : (
                  notification.DefaultActionName &&
                  notification.DefaultActionUrl && (
                    <button
                      onClick={handleNotificationClick}
                      data-action="notification-action-link"
                      data-name="notification-prolong-button"
                      className={cx(
                        "glz-button",
                        "glz-is-primary",
                        "glz-is-small"
                      )}
                    >
                      {notification.DefaultActionName}
                    </button>
                  )
                )}

                <div className={cx("date-space")}>
                  <span
                    className={cx("message-date")}
                    data-name="notification-message-date"
                  >
                    {formatDate(notification.CreatedOn)}
                  </span>
                  <span
                    id={`ntf-mark-badge-${notification.ID}`}
                    className={cx("message-badge")}
                  />
                </div>
              </div>

              {PartnerId && PartnerName && (
                <div className={cx("unsubscribe")}>
                  {CanUnsubscribe
                    ? interpolate(strings.notification_unsubscribePartnerText, {
                        val1: (
                          <Link
                            className={cx("unsubscribe-link")}
                            onClick={handleUnsubscribe}
                          >
                            {strings.notification_unsubscribePartnerATISU}
                          </Link>
                        ),
                        val2: (
                          <Link
                            className={cx("unsubscribe-link")}
                            href={settingsUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {strings.notification_notificationsSettings}
                          </Link>
                        ),
                      })
                    : interpolate(strings.youCanUnsubscribeFromPartners, {
                        inSettings: (
                          <span className={cx("no-wrap")}>
                            <Link
                              href={settingsUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              {strings.inNotificationSettings}
                            </Link>
                          </span>
                        ),
                      })}
                  {}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <div className={cx("breaker")} />
    </>
  );
};

Notification.propTypes = {
  notification: PropTypes.object.isRequired,
  reference: PropTypes.func,
};

export default observer(Notification);
